import { useEffect, useMemo, useState } from 'react';
import { FosSidebar } from './Sidebar';
import { FosHeader } from './Header';
import { FosCommandPalette } from './CommandPalette';
import { FosTweaksContext, useFosTweaks, type FosTweaksContextValue } from './TweaksContext';
import { TourProvider, TourEngine } from '@/components/onboarding/flipperos';
import { EbayNotConnectedBanner } from '@/components/shell/EbayNotConnectedBanner';
import { useSaleArrivalToast } from '@/components/shell/useSaleArrivalToast';

interface AppShellProps {
  children: React.ReactNode;
}

const SIDEBAR_KEY = 'forvisibility.sidebar.collapsed.v1';

/** For Visibility-styled application shell. Wraps a Sidebar + Header around the
 *  page content, hosts the ⌘K command palette, and exposes the floating
 *  Tweaks panel. Designed to coexist with the existing AppLayout — pages can
 *  opt into this shell at the route level when retrofitted. */
export function FosAppShell({ children }: AppShellProps) {
  const { tweaks, setTweak } = useFosTweaks();

  // One global subscription to sales-table inserts, so the user gets a
  // toast the moment a sale lands (closes UX analysis A5). Mounted at
  // the shell layer so navigation doesn't tear down + recreate the
  // channel between pages.
  useSaleArrivalToast();

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(SIDEBAR_KEY) === '1';
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(SIDEBAR_KEY, collapsed ? '1' : '0');
  }, [collapsed]);

  const [cmdOpen, setCmdOpen] = useState(false);

  // Global ⌘K / Ctrl+K toggle. Only attached once per shell mount.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const meta = e.metaKey || e.ctrlKey;
      if (meta && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setCmdOpen((p) => !p);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const ctxValue = useMemo<FosTweaksContextValue>(() => ({ tweaks, setTweak }), [tweaks, setTweak]);

  return (
    <FosTweaksContext.Provider value={ctxValue}>
      <TourProvider>
        <div className="fos-surface flex h-screen overflow-hidden bg-fos-bg-base font-sans text-fos-text-primary">
          <FosSidebar collapsed={collapsed} onToggleCollapsed={() => setCollapsed((c) => !c)} />

          <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
            <FosHeader onOpenCommand={() => setCmdOpen(true)} />

            <EbayNotConnectedBanner />

            <main className="fos-scrollbar flex-1 overflow-auto p-5">{children}</main>
          </div>

          <FosCommandPalette open={cmdOpen} onOpenChange={setCmdOpen} />
          <TourEngine />
        </div>
      </TourProvider>
    </FosTweaksContext.Provider>
  );
}
